import React from "react";
import {
  CalendarDays,
  Check,
  ClipboardList,
  ExternalLink,
  FileText,
  FileType,
  MapPin,
  UserRound,
  X,
} from "lucide-react";
import {
  openFormFile,
  resolveCategoryLabel,
  resolveDestinationCountry,
} from "./companyFormConstants";

const ICON_STROKE = 1.75;

function resolveFileKind(item) {
  const hint = `${item.file_path || ""} ${item.file_url || ""}`.toLowerCase();
  if (hint.includes(".pdf")) return "pdf";
  if (hint.includes(".doc") || hint.includes(".docx")) return "word";
  return "file";
}

function formatUploadedAt(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const datePart = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${datePart} • ${timePart}`;
}

function MetaLine({ icon: Icon, children }) {
  return (
    <div className="flex min-w-0 items-center gap-2 text-[12px] leading-tight text-[#64748B]">
      <Icon className="shrink-0 text-[#94A3B8]" size={14} strokeWidth={ICON_STROKE} aria-hidden />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

export default function DocumentApprovalCard({
  item,
  busy,
  categoryLabels,
  onApprove,
  onReject,
  selectionEnabled = false,
  selected = false,
  onToggleSelect,
}) {
  const formName = item.form?.name || "Untitled document";
  const category = resolveCategoryLabel(item.form?.category, categoryLabels);
  const destination = resolveDestinationCountry(item.form?.destination_country);
  const uploader = item.uploaded_by?.name;
  const fileKind = resolveFileKind(item);

  const fileTileClass =
    fileKind === "word"
      ? "border-[#BFDBFE] bg-[#EFF6FF] text-[#2563EB]"
      : fileKind === "pdf"
        ? "border-[#FECACA] bg-[#FEF2F2] text-[#DC2626]"
        : "border-[#E2E8F0] bg-[#F8FAFC] text-[#64748B]";

  const FileIcon = fileKind === "word" ? FileType : FileText;

  const btn =
    "inline-flex h-8 min-w-0 flex-1 items-center justify-center gap-1 rounded-lg px-1 text-[11px] font-semibold transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <li
      className={`min-w-0 rounded-xl border bg-white p-3.5 shadow-[0_1px_2px_rgba(20,33,61,0.05)] ${
        selected ? "border-[#007BFF] ring-1 ring-[#007BFF]/25" : "border-[#E5ECF5]"
      }`}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          {selectionEnabled ? (
            <input
              type="checkbox"
              className="mt-2 h-3.5 w-3.5 shrink-0 cursor-pointer accent-[#007BFF]"
              checked={selected}
              disabled={busy}
              onChange={() => onToggleSelect?.(item)}
              aria-label={`Select ${formName} for bulk review`}
            />
          ) : null}
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${fileTileClass}`}
          >
            <FileIcon size={20} strokeWidth={ICON_STROKE} aria-hidden />
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[10px] font-semibold text-[#059669]">
          New
        </span>
      </div>

      <h3 className="mb-2 text-[14px] font-semibold leading-snug text-[#14213D] break-words">
        {formName}
      </h3>

      <div className="space-y-1.5">
        <MetaLine icon={MapPin}>{destination}</MetaLine>
        <MetaLine icon={ClipboardList}>{category}</MetaLine>
        <MetaLine icon={UserRound}>
          {uploader ? `Uploaded by ${uploader}` : "Uploaded by —"}
        </MetaLine>
      </div>

      <div className="my-2.5 border-t border-[#E5ECF5]" />

      <div className="mb-2.5 flex min-w-0 items-center gap-2 text-[12px] leading-tight text-[#64748B]">
        <CalendarDays
          className="shrink-0 text-[#94A3B8]"
          size={14}
          strokeWidth={ICON_STROKE}
          aria-hidden
        />
        <span className="min-w-0 break-words">{formatUploadedAt(item.created_at)}</span>
      </div>

      <div className="grid grid-cols-3 gap-1.5" role="group" aria-label={`Actions for ${formName}`}>
        {item.file_url ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => openFormFile(item)}
            className={`${btn} border border-[#BFDBFE] bg-[#EFF6FF] text-[#007BFF] hover:bg-[#DBEAFE]/60`}
          >
            <ExternalLink size={12} strokeWidth={ICON_STROKE} aria-hidden />
            Open
          </button>
        ) : (
          <button
            type="button"
            disabled
            className={`${btn} border border-[#E2E8F0] bg-[#F8FAFC] text-[#94A3B8]`}
          >
            <ExternalLink size={12} strokeWidth={ICON_STROKE} aria-hidden />
            Open
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={() => onApprove(item)}
          className={`${btn} border-0 bg-[#007BFF] text-white hover:bg-[#0069d9]`}
        >
          <Check size={12} strokeWidth={ICON_STROKE} aria-hidden />
          Approve
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onReject(item)}
          className={`${btn} border border-[#FECACA] bg-white text-[#EF4444] hover:bg-[#FEF2F2]`}
        >
          <X size={12} strokeWidth={ICON_STROKE} aria-hidden />
          Reject
        </button>
      </div>
    </li>
  );
}
